import React, { useMemo, useState, useEffect } from "react";

import { format, isValid, parse, parseISO } from "date-fns";
import { Pencil, Trash2 } from "lucide-react";

import Button from "../../../Components/Button";
import DatePicker from "../../../Components/DatePicker";
import CommonInput from "../../../Components/Input";
import Select from "../../../Components/Select";
import StepProgressBar from "../../../Components/StepProgressBar";
import Table from "../../../Components/Table";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import { fetchAdminUsers } from "../../../Store/Reducers/MasterSlice";
import {
  createAccountsBulk,
  CustomerPayload,
  BulkCreateAccountsPayload,
  clearAccountsError,
  clearAccountsSuccess,
  resetAccountsState,
  ActiveCustomerWithSponsorsItem,
} from "../../../Store/Reducers/OpsSlice";
import { isValidEmail } from "../../../Utils/Constants/RegexConstants";
import {
  normalizeRoleName,
  ROLE_DISPLAY_NAMES,
} from "../../../Utils/Constants/RoleConstants";
import { useAccountTiers } from "../../../Utils/Helper/accountTierHelpers";

type AccountForm = {
  name: string;
  industry: string;
  tier: string;
  city: string;
  country: string;
  contactPerson: string;
  contactEmail: string;
  contactPhone: string;
  employees: string;
  revenue: string;
  renewalDate: string;
  churnRisk: string;
};

const emptyForm: AccountForm = {
  name: "",
  industry: "",
  tier: "",
  city: "",
  country: "",
  contactPerson: "",
  contactEmail: "",
  contactPhone: "",
  employees: "",
  revenue: "",
  renewalDate: "",
  churnRisk: "",
};

const churnRiskOptions = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
];

/** Normalize API/display dates to YYYY-MM-DD for DatePicker + fiscal_year_end. */
const toFiscalYearEndDate = (value?: string | null): string => {
  if (!value?.trim()) return "";
  const trimmed = value.trim();

  let parsed: Date | undefined;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    // Use date-only parse so "2026-05-10T00:00:00" stays May 10 (no TZ shift)
    parsed = parse(trimmed.slice(0, 10), "yyyy-MM-dd", new Date());
  } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    parsed = parse(trimmed, "MM/dd/yyyy", new Date());
  } else {
    parsed = parseISO(trimmed);
  }

  if (!isValid(parsed)) return "";
  return format(parsed, "yyyy-MM-dd");
};

type AddAccountProps = {
  onClose: () => void;
  accountToEdit: ActiveCustomerWithSponsorsItem | null;
};

const AddAccount: React.FC<AddAccountProps> = ({ onClose, accountToEdit }) => {
  const isEditMode = !!accountToEdit;
  const dispatch = useAppDispatch();
  const { tierOptions, formatTier } = useAccountTiers();
  const { loading, error, success } = useAppSelector(
    state => state.ops.accounts
  );
  const generateDefaultBatchName = () =>
    accountToEdit
      ? `Accounts Update - ${new Date().toLocaleString()}`
      : `Accounts Add - ${new Date().toLocaleString()}`;

  // Pre-fill form when accountToEdit is provided
  useEffect(() => {
    if (accountToEdit) {
      // Update form data
      const formData = {
        name: accountToEdit.name || "",
        industry: accountToEdit.industry || "", // Industry might need to be fetched from another API
        tier: accountToEdit.tiers || "",
        city: accountToEdit.city || "",
        country: accountToEdit.country || accountToEdit.region || "",
        contactPerson: accountToEdit.contact_person || "",
        contactEmail: accountToEdit.email || "",
        contactPhone: accountToEdit.phone || "",
        employees: accountToEdit.employees?.toString() || "",
        revenue: accountToEdit.revenue?.toString() || "",
        renewalDate: toFiscalYearEndDate(accountToEdit.renewal),
        churnRisk: accountToEdit.churn_risk || "",
      };

      // Set form data
      setForm(formData);

      // Initialize accounts list with the account being edited
      setAccounts([formData]);

      // Update batch name for edit mode
      const defaultName = `Accounts Update - ${new Date().toLocaleString()}`;
      setBatchName(defaultName);
      setDefaultBatchName(defaultName);
      setEditingIndex(0);
    }
  }, [accountToEdit]);
  const { items: adminUsers } = useAppSelector(
    state => state.master.adminUsers
  );

  const [form, setForm] = useState<AccountForm>(emptyForm);
  const [accounts, setAccounts] = useState<AccountForm[]>([]);
  const [step, setStep] = useState<1 | 2>(1);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [batchName, setBatchName] = useState(() => generateDefaultBatchName());
  const [defaultBatchName, setDefaultBatchName] = useState(() =>
    generateDefaultBatchName()
  );
  const [emailError, setEmailError] = useState<string>("");

  // Transform admin users to approver options
  const approverOptions = useMemo(() => {
    return adminUsers.map(user => {
      const roleName = user.role || "";
      const normalizedRole = normalizeRoleName(roleName);
      const roleDisplay =
        ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES] ||
        roleName ||
        "";

      return {
        label: `${user.full_name} - ${roleDisplay}`,
        value: user.user_id,
      };
    });
  }, [adminUsers]);

  const [approver, setApprover] = useState<string>("");

  // Fetch admin users on component mount
  useEffect(() => {
    if (adminUsers.length === 0) {
      dispatch(fetchAdminUsers());
    }
  }, [dispatch, adminUsers.length]);

  // Set default approver when options are available
  useEffect(() => {
    if (approverOptions.length > 0 && !approver) {
      setApprover(approverOptions[0].value);
    }
  }, [approverOptions, approver]);

  // Map form data to customer payload
  const mapFormToCustomerPayload = (formData: AccountForm): CustomerPayload => {
    const payload: CustomerPayload = {
      name: formData.name,
      email: formData.contactEmail,
      phone: formData.contactPhone,
      contact_person: formData.contactPerson,
      churn_risk: formData.churnRisk,
      city: formData.city,
      country: formData.country,
      tier: formData.tier,
      industry: formData.industry,
    };

    // Only include employees if provided and convert to number
    if (formData.employees && formData.employees.trim() !== "") {
      const employeesNum = parseInt(formData.employees, 10);
      if (!isNaN(employeesNum)) {
        payload.employees = employeesNum;
      }
    }

    // Only include revenue if provided and convert to float
    if (formData.revenue && formData.revenue.trim() !== "") {
      const revenueNum = parseFloat(formData.revenue);
      if (!isNaN(revenueNum)) {
        payload.revenue = revenueNum;
      }
    }

    // fiscal_year_end must be YYYY-MM-DD (backend maps it to renewal_date)
    const fiscalYearEnd = toFiscalYearEndDate(formData.renewalDate);
    if (fiscalYearEnd) {
      payload.fiscal_year_end = fiscalYearEnd;
    }

    return payload;
  };

  // Handle success/error
  useEffect(() => {
    if (success) {
      dispatch(resetAccountsState());
      onClose();
    }
  }, [success, dispatch, onClose]);

  // Clear error on unmount
  useEffect(() => {
    return () => {
      dispatch(resetAccountsState());
    };
  }, [dispatch]);

  const canAdd = useMemo(() => {
    return (
      form.name.trim() !== "" &&
      form.industry.trim() !== "" &&
      form.tier.trim() !== "" &&
      form.churnRisk.trim() !== "" &&
      form.city.trim() !== "" &&
      form.country.trim() !== "" &&
      form.contactPerson.trim() !== "" &&
      form.contactEmail.trim() !== "" &&
      isValidEmail(form.contactEmail) &&
      !emailError
    );
  }, [form, emailError]);

  const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  const handleChange =
    (key: keyof AccountForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isEditMode && key === "name") return;
      const value = e.target.value;
      setForm(prev => ({ ...prev, [key]: value }));

      // Validate email on change
      if (key === "contactEmail") {
        if (value.trim() === "") {
          setEmailError("");
        } else if (!isValidEmail(value)) {
          setEmailError("Please enter a valid email address");
        } else {
          setEmailError("");
        }
      }
    };

  const handleAddToList = () => {
    if (isEditMode || !canAdd) return;
    if (editingIndex !== null) {
      setAccounts(prev => prev.map((a, i) => (i === editingIndex ? form : a)));
      setEditingIndex(null);
    } else {
      setAccounts(prev => [...prev, form]);
    }
    setForm(emptyForm);
    setEmailError("");
  };

  const handleDeleteFromList = (index: number) => {
    if (isEditMode) return;
    setAccounts(prev => prev.filter((_, i) => i !== index));
  };

  // In edit mode, keep the single account synced with form changes.
  useEffect(() => {
    if (!isEditMode) return;
    setAccounts([form]);
  }, [form, isEditMode]);

  const handleEditFromList = (index: number) => {
    const selected = accounts[index];
    setForm(selected);
    setEditingIndex(index);
    // Clear email error when editing (will re-validate on change)
    setEmailError("");
  };

  const handleReview = () => {
    if (isEditMode) {
      if (!canAdd) return;
      setAccounts([form]);
    }
    setStep(2);
  };
  const handleSubmit = async () => {
    if (accounts.length === 0 || !approver) return;

    dispatch(clearAccountsError());
    dispatch(clearAccountsSuccess());

    // Map all accounts to customer payload format
    const customers = accounts.map(mapFormToCustomerPayload);

    const resolvedBatchName = batchName.trim();
    const batchNameForBackend =
      !resolvedBatchName || resolvedBatchName === defaultBatchName.trim()
        ? null
        : resolvedBatchName;

    // Create bulk payload
    const bulkPayload = {
      batch_name: batchNameForBackend,
      approver_id: approver,
      customers: customers,
      upload_type: isEditMode ? "customer_update" : "add_customer",
    };

    // Dispatch bulk create action
    await dispatch(
      createAccountsBulk(bulkPayload as BulkCreateAccountsPayload)
    );
  };

  return (
    <div>
      <div className="h-[calc(100vh-9.5rem)] overflow-x-visible overflow-y-auto p-4">
        <div
          className="mb-4 overflow-x-visible min-w-0"
          style={{ ["--step-track-bg" as string]: "#fff" }}
        >
          <StepProgressBar
            steps={[
              { label: isEditMode ? "Edit Account" : "Add Accounts" },
              { label: "Review & Select Approver" },
            ]}
            currentStepIndex={step}
          />
        </div>

        <div className="pt-4">
          {step === 1 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-5">
                  <CommonInput
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange("name")}
                    required
                    disabled={isEditMode}
                    infoText={
                      isEditMode
                        ? "Account name cannot be edited"
                        : "Max 100 characters"
                    }
                    placeholder="Account Name"
                  />
                </div>
                <div className="mb-5">
                  <CommonInput
                    label="Industry"
                    name="industry"
                    value={form.industry}
                    onChange={handleChange("industry")}
                    required
                    infoText="Max 100 characters"
                    placeholder="Industry"
                  />
                </div>

                <div className="mb-5">
                  <Select
                    label="Tier"
                    name="tier"
                    value={form.tier}
                    options={tierOptions}
                    onChange={handleChange("tier")}
                    required
                  />
                </div>
                <div className="mb-5">
                  <Select
                    label="Churn Risk"
                    name="churnRisk"
                    value={form.churnRisk}
                    options={churnRiskOptions}
                    onChange={handleChange("churnRisk")}
                    required
                  />
                </div>
                <div className="mb-5">
                  <CommonInput
                    label="Country"
                    name="country"
                    value={form.country}
                    onChange={handleChange("country")}
                    required
                    placeholder="Country"
                  />
                </div>

                <div className="mb-5">
                  <CommonInput
                    label="City"
                    name="city"
                    value={form.city}
                    onChange={handleChange("city")}
                    required
                    placeholder="City"
                  />
                </div>

                <div className="mb-5">
                  <CommonInput
                    label="Contact Person"
                    name="contactPerson"
                    value={form.contactPerson}
                    onChange={handleChange("contactPerson")}
                    required
                    infoText="Max 100 characters, alphanumeric and spaces only"
                    placeholder="Contact Person"
                  />
                </div>
                <div className="mb-5">
                  <CommonInput
                    label="Contact Email"
                    name="contactEmail"
                    type="email"
                    value={form.contactEmail}
                    onChange={handleChange("contactEmail")}
                    required
                    error={emailError}
                    placeholder="Contact Email"
                  />
                </div>

                <div className="mb-5">
                  <CommonInput
                    label="Contact Phone"
                    name="contactPhone"
                    value={form.contactPhone}
                    onChange={handleChange("contactPhone")}
                    placeholder="Contact Phone"
                  />
                </div>
                <div className="mb-5">
                  <CommonInput
                    label="Revenue"
                    name="revenue"
                    value={form.revenue}
                    onChange={handleChange("revenue")}
                    placeholder="Revenue"
                  />
                </div>

                <div className="mb-5">
                  <CommonInput
                    label="Employees"
                    name="employees"
                    value={form.employees}
                    onChange={handleChange("employees")}
                    placeholder="Employees"
                  />
                </div>
                <div className="mb-5">
                  <DatePicker
                    label="Renewal Date"
                    name="renewalDate"
                    value={form.renewalDate}
                    onChange={handleChange("renewalDate")}
                    min={today}
                    infoText="Only future dates (today or later) are allowed"
                  />
                </div>
              </div>

              {!isEditMode && accounts.length > 0 && (
                <div>
                  <h3 className="text-base font-semibold text-gray-800 mb-3">
                    Accounts to be Added ({accounts.length})
                  </h3>
                  {accounts.map((a, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-4 p-3.5 px-4 border border-gray-200 rounded-lg mb-3 last:mb-0 bg-white"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[0.9375rem] leading-snug text-gray-800 font-semibold">
                          {a.name}
                        </div>
                        <div className="text-[0.8125rem] leading-snug mt-1 text-gray-500">
                          {a.city || "NA"} • {formatTier(a.tier) || a.tier} •{" "}
                          {a.contactPerson} ({a.contactEmail})
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="clear"
                          onClick={() => handleEditFromList(idx)}
                          title="Edit"
                          className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        >
                          <Pencil size={17} />
                        </Button>
                        <Button
                          variant="clear"
                          onClick={() => handleDeleteFromList(idx)}
                          title="Delete"
                          className="inline-flex items-center justify-center p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Bottom actions handled in global footer */}
            </>
          )}

          {step === 2 && (
            <div>
              <p className="mb-3 fw-600">
                {isEditMode
                  ? "Review Account"
                  : `Review Accounts (${accounts.length})`}
              </p>
              <Table
                className="mb-4 border rounded-lg"
                columns={[
                  { key: "name", title: "Name" },
                  { key: "industry", title: "Industry" },
                  {
                    key: "tier",
                    title: "Tier",
                    render: (value: string) =>
                      formatTier(value) || value || "-",
                  },
                  {
                    key: "contact",
                    title: "Contact",
                    render: (_: any, record: any) => (
                      <div>
                        <div>{record.contactPerson}</div>
                        <div className="text-[0.8125rem] text-gray-500">
                          {record.contactEmail}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "location",
                    title: "Location",
                    render: (_: any, record: any) =>
                      [record.city, record.country].filter(Boolean).join(", "),
                  },
                ]}
                data={accounts as any}
              />

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
                <h4 className="text-[0.9375rem] font-bold mb-4">
                  Approval Required
                </h4>
                <CommonInput
                  className="mb-6"
                  name="batchName"
                  value={batchName}
                  onChange={e => setBatchName(e.target.value)}
                  label="Batch Name (Optional)"
                />

                <Select
                  name="approver"
                  value={approver}
                  options={approverOptions}
                  onChange={e => setApprover(String(e.target.value))}
                  isShowPlaceHolder={false}
                  required
                  disabled={approverOptions.length === 0}
                  label="Select Approver (Admin, C-Level, or Executive Leader)"
                  className="mb-4"
                />
                <p className="text-[0.8125rem] text-gray-500 mt-3 leading-snug">
                  These {accounts.length} account
                  {accounts.length !== 1 ? "s" : ""} will be sent for approval
                  before activation
                </p>
                {error && (
                  <div
                    className="alert alert-danger mt-4 rounded-md"
                    role="alert"
                  >
                    {error}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between items-center gap-4 flex-wrap min-h-[2.75rem] mt-4 pt-3 border-t px-3">
        {step === 1 && (
          <>
            <Button
              variant="outline-primary"
              className="btn-lg min-h-[2.5rem]"
              onClick={onClose}
            >
              Cancel
            </Button>
            <div className="flex items-center gap-2">
              {!isEditMode && (
                <Button
                  variant="primary"
                  className="btn-lg min-h-[2.5rem]"
                  onClick={handleAddToList}
                  disabled={!canAdd}
                >
                  {editingIndex !== null
                    ? "Update Account in List"
                    : "+ Add to List"}
                </Button>
              )}
              <Button
                variant="primary"
                className="btn-lg min-h-[2.5rem]"
                onClick={handleReview}
                disabled={isEditMode ? !canAdd : accounts.length === 0}
              >
                {isEditMode ? "Review Changes" : "Review Accounts"}
              </Button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <Button
              variant="outline-primary"
              className="btn-lg min-h-[2.5rem]"
              onClick={() => setStep(1)}
            >
              Back
            </Button>
            <Button
              variant="primary"
              className="btn-lg min-h-[2.5rem]"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit for Approval"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default AddAccount;
